import { oc } from "@orpc/contract";
import { RelayAgentCommandTypeSchema } from "@erp/shared-types";
import { z } from "zod";

/**
 * Relay command-channel contracts.
 *
 * `sendAgentCommand` lets an operator push a control command to the on-prem sync
 * relay agent bound to a given RelayIntegration. The backend resolves the
 * integration's owning ApiKey (agent identity) and publishes the command to that
 * agent's per-agent RabbitMQ queue.
 *
 * The whole command vocabulary is `sync_now` and `stop` — see
 * `relayAgentCommands.ts` for why. Everything else an operator might want to
 * change (cursor, enabled, query, schedule) is a write to the ERP's own record,
 * which the agent reads on its next sync. `resetCursor` below is exactly that: a
 * backend-local write the handler performs *before* publishing, not a second
 * command for the agent to carry out.
 */
export const sendAgentCommandInputSchema = z
  .object({
    relayIntegrationId: z.string().uuid(),
    commandType: RelayAgentCommandTypeSchema,
    /**
     * Clear the integration's stored cursor before triggering, making the run a
     * full re-export rather than an incremental one. Only meaningful with
     * `sync_now`.
     */
    resetCursor: z.boolean().default(false),
  })
  .refine((v) => !v.resetCursor || v.commandType === "sync_now", {
    message: "resetCursor is only valid with commandType 'sync_now'",
    path: ["resetCursor"],
  });

const SendAgentCommandOutputSchema = z.object({
  success: z.literal(true),
  /** The generated command id (uuid) that was enqueued. */
  commandId: z.string().uuid(),
  /** Whether the integration's cursor was cleared before publishing. */
  cursorReset: z.boolean(),
});

export const relayContracts = {
  sendAgentCommand: oc
    .input(sendAgentCommandInputSchema)
    .output(SendAgentCommandOutputSchema)
    .route({
      summary: "Send a command to an on-prem sync agent",
      description:
        "Queues a sync-now or stop command for the on-premises data sync agent of a relay integration; can first reset its cursor to force a full re-export.",
      method: "POST",
    }),
} as const;

export type SendAgentCommandInput = z.infer<typeof sendAgentCommandInputSchema>;
export type SendAgentCommandOutput = z.infer<
  typeof SendAgentCommandOutputSchema
>;
